import { useEffect, useRef, Dispatch, SetStateAction, RefObject } from 'react';
import { toast } from 'react-toastify';
import { logger } from '@/utils/logger';
import { getRandomEncouragementMessage } from '../constants';

export interface NoteData {
  id?: string;
  note_id?: string;
  document_type?: string;
  [key: string]: unknown;
}

export interface Message {
  sender: string;
  text?: string;
  options?: string[];
  note_data?: NoteData;
  batch_notes?: NoteData[] | null;
  [key: string]: unknown;
}

interface EventDetail extends Record<string, unknown> {
  message?: string;
  noteData?: NoteData;
  batchNotes?: NoteData[];
  requestId?: string;
  sessionId?: string;
  errorType?: string;
  full_message?: string;
}

interface UseNoteCompletionEventsOptions {
  currentSessionId: string | null | undefined;
  currentRequestId: string | null | undefined;
  clearCurrentRequestId: () => void;
  setIsProcessing?: (processing: boolean) => void;
  setMessages: Dispatch<SetStateAction<Message[]>>;
  setBatchNotes: Dispatch<SetStateAction<NoteData[] | null>>;
  setCurrentNoteIndex: Dispatch<SetStateAction<number>>;
  guidedMode: boolean;
  guidedWorkflowData: unknown;
  messagesEndRef: RefObject<HTMLDivElement | null>;
}

export function useNoteCompletionEvents({
  currentSessionId,
  currentRequestId,
  clearCurrentRequestId,
  setIsProcessing,
  setMessages,
  setBatchNotes,
  setCurrentNoteIndex,
  guidedMode,
  guidedWorkflowData,
  messagesEndRef,
}: UseNoteCompletionEventsOptions) {
  const seenSoapRequestIdsRef = useRef(new Set<string>());
  const seenSoapNoteIdsRef = useRef(new Set<string>());
  const seenPreambleRequestIdsRef = useRef(new Set<string>());

  // Mutable refs mirror the latest prop/state values so the window event
  // listeners never act on stale closures. This removes a race where a
  // soap_note_preamble / complete / error event arrives between the moment
  // the active request id is set and the effect cleanup/re-attach cycle.
  const currentRequestIdRef = useRef(currentRequestId);
  const currentSessionIdRef = useRef(currentSessionId);
  const clearCurrentRequestIdRef = useRef(clearCurrentRequestId);
  const guidedModeRef = useRef(guidedMode);
  const guidedWorkflowDataRef = useRef(guidedWorkflowData);

  currentRequestIdRef.current = currentRequestId;
  currentSessionIdRef.current = currentSessionId;
  clearCurrentRequestIdRef.current = clearCurrentRequestId;
  guidedModeRef.current = guidedMode;
  guidedWorkflowDataRef.current = guidedWorkflowData;

  useEffect(() => {
    const handleSoapNoteComplete = (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;
      const { message, noteData, batchNotes, requestId, sessionId: eventSessionId } = customEvent.detail || {};
      const noteId = noteData?.note_id || noteData?.id;

      logger.log('[THERAPIST_PANEL] soap_note_complete requestId=%s sessionId=%s noteId=%s batchCount=%d', requestId, eventSessionId, noteId, batchNotes?.length || 0);

      if (currentSessionIdRef.current && eventSessionId && eventSessionId !== currentSessionIdRef.current) {
        logger.log('[THERAPIST_PANEL] Ignoring completion for different session:', eventSessionId);
        return;
      }
      if (!currentRequestIdRef.current || !requestId || requestId !== currentRequestIdRef.current) {
        logger.log('[THERAPIST_PANEL] Ignoring completion: no active request or mismatched requestId:', requestId);
        return;
      }
      if (requestId && seenSoapRequestIdsRef.current.has(requestId)) {
        logger.log('[THERAPIST_PANEL] Ignoring duplicate completion for request:', requestId);
        return;
      }
      if (noteId && seenSoapNoteIdsRef.current.has(noteId)) {
        logger.log('[THERAPIST_PANEL] Ignoring duplicate completion for note:', noteId);
        return;
      }

      if (requestId) seenSoapRequestIdsRef.current.add(requestId);
      if (noteId) seenSoapNoteIdsRef.current.add(noteId);

      const assistantMessage = {
        sender: 'assistant',
        text: message,
        note_data: noteData,
        batch_notes: batchNotes,
      };
      setMessages(prev => [...prev, assistantMessage]);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

      if (batchNotes && batchNotes.length > 1) {
        setBatchNotes(batchNotes);
        setCurrentNoteIndex(0);
        logger.log('[THERAPIST_PANEL] Multi-note generation detected:', batchNotes.length, 'notes');
      } else {
        setBatchNotes(null);
        setCurrentNoteIndex(0);
      }

      if (guidedModeRef.current && guidedWorkflowDataRef.current) {
        toast.success(getRandomEncouragementMessage(), {
          position: 'top-center',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }

      setIsProcessing?.(false);
      clearCurrentRequestIdRef.current();
    };

    window.addEventListener('soap_note_complete', handleSoapNoteComplete);
    return () => window.removeEventListener('soap_note_complete', handleSoapNoteComplete);
  }, [setMessages, setBatchNotes, setCurrentNoteIndex, messagesEndRef, setIsProcessing]);

  useEffect(() => {
    const handleSoapNoteError = (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;
      const { message, requestId, sessionId: eventSessionId, errorType } = customEvent.detail || {};

      logger.error('[THERAPIST_PANEL] soap_note_error requestId=%s sessionId=%s errorType=%s', requestId, eventSessionId, errorType);

      if (currentSessionIdRef.current && eventSessionId && eventSessionId !== currentSessionIdRef.current) {
        logger.log('[THERAPIST_PANEL] Ignoring error for different session:', eventSessionId);
        return;
      }
      if (!currentRequestIdRef.current || !requestId || requestId !== currentRequestIdRef.current) {
        logger.log('[THERAPIST_PANEL] Ignoring error: no active request or mismatched requestId:', requestId);
        return;
      }
      if (requestId && seenSoapRequestIdsRef.current.has(requestId)) {
        logger.log('[THERAPIST_PANEL] Ignoring duplicate error for request:', requestId);
        return;
      }

      if (requestId) seenSoapRequestIdsRef.current.add(requestId);

      setMessages(prev => prev.map(message => (
        message.sender === 'user' && message.request_id === requestId
          ? { ...message, status: 'failed' }
          : message
      )));

      const assistantMessage = {
        sender: 'assistant',
        text: message || 'Note generation failed. Please try again with more session details.',
        errorType,
      };
      setMessages(prev => [...prev, assistantMessage]);
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

      setIsProcessing?.(false);
      clearCurrentRequestIdRef.current();
    };

    const handleSoapNotePreamble = (event: Event) => {
      const customEvent = event as CustomEvent<EventDetail>;
      const { message, full_message: fullMessage, requestId, sessionId: eventSessionId } = customEvent.detail || {};

      logger.log('[THERAPIST_PANEL] soap_note_preamble requestId=%s sessionId=%s', requestId, eventSessionId);

      if (currentSessionIdRef.current && eventSessionId && eventSessionId !== currentSessionIdRef.current) {
        logger.log('[THERAPIST_PANEL] Ignoring preamble for different session:', eventSessionId);
        return;
      }
      if (!currentRequestIdRef.current || !requestId || requestId !== currentRequestIdRef.current) {
        logger.log('[THERAPIST_PANEL] Ignoring preamble: no active request or mismatched requestId:', requestId);
        return;
      }
      if (requestId && seenPreambleRequestIdsRef.current.has(requestId)) {
        logger.log('[THERAPIST_PANEL] Ignoring duplicate preamble for request:', requestId);
        return;
      }

      if (requestId) seenPreambleRequestIdsRef.current.add(requestId);

      const preambleText = fullMessage || message;
      if (preambleText) {
        const assistantMessage = { sender: 'assistant', text: preambleText };
        setMessages(prev => [...prev, assistantMessage]);
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    };

    window.addEventListener('soap_note_error', handleSoapNoteError);
    window.addEventListener('soap_note_preamble', handleSoapNotePreamble);
    return () => {
      window.removeEventListener('soap_note_error', handleSoapNoteError);
      window.removeEventListener('soap_note_preamble', handleSoapNotePreamble);
    };
  }, [setMessages, messagesEndRef, setIsProcessing]);
}
